import { Button, Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { activeFilterCount, type EmployeeFilters } from "../hooks/useEmployeeFilters";
import type { ZoneOption } from "../hooks/useEmployeesList";
import { HR_STATUSES, hrStatusLabel } from "../types";

interface Option {
	id: string;
	name: string;
}

/**
 * ⚠️ **Kanvasdagi «to'ldirilgan chip» — antd `Select` ning O'ZI.** Qiymat
 * bo'lganda `labelRender` yorliqni oldiga qo'yadi (`Bo'lim: Kredit
 * bo'limi`) va shu klass foni beradi; bo'sh bo'lganda oddiy `placeholder`
 * (kanvas: «Lavozim»). Ranglar kanvasdan: fon `#eaf1fb`, chegara
 * `#c9dbf2`, matn `#1c5cab`.
 *
 * ⚠️ Chip'ning `×` i — `allowClear`. Alohida `×` tugmasi yasalmaydi.
 */
const FILLED_CLASS =
	"[&_.ant-select-selector]:!bg-[#eaf1fb] [&_.ant-select-selector]:!border-[#c9dbf2] [&_.ant-select-selection-item]:!text-[#1c5cab]";

function chipClass(filled: boolean): string | undefined {
	return filled ? FILLED_CLASS : undefined;
}

/**
 * Chip ko'rinishidagi filtr qatori.
 *
 * ⚠️ Komponent holat SAQLAMAYDI — `filters` prop, `onChange` chiqish.
 *
 * ⚠️⚠️ **Filtr o'zgarganda `page` HAR DOIM 1 ga qaytadi.** Ilgari buni har
 * `Select` o'zi qilardi (`EmployeesPage.tsx` da to'rt joyda); bir joyga
 * yig'ilmasa foydalanuvchi 3-sahifada filtr o'zgartirib bo'sh jadval
 * ko'rardi.
 */
export function EmployeeFilterBar({
	filters,
	onChange,
	departments,
	positions,
	zones,
}: {
	filters: EmployeeFilters;
	onChange: (next: EmployeeFilters) => void;
	departments: Option[];
	positions: Option[];
	zones: ZoneOption[];
}) {
	const { t } = useTranslation("employees");

	const patch = (next: Partial<EmployeeFilters>) => {
		onChange({ ...filters, ...next, page: 1 });
	};

	const hrStatusOptions = HR_STATUSES.map((status) => ({
		value: status,
		label: hrStatusLabel(t, status),
	}));

	/**
	 * ⚠️ «Xavf» filtri — YAGONA variant, uch bandli shkala EMAS.
	 * `computeRiskScore` (`packages/domain/src/risk.ts`) shubha hodisalari
	 * vaznlarining CHEGARALANMAGAN yig'indisi, ya'ni 0–100 shkalasi ham,
	 * «sariq» oralig'i ham yo'q. Yagona haqiqiy chegara — `riskFlag`.
	 */
	const riskOptions = [{ value: "true", label: t("filterBar.riskOnly") }];

	return (
		<div className="flex flex-wrap items-center gap-2 px-3.5 py-2.5">
			{/*
			 * ⚠️ Placeholder kanvasdan AYNAN ko'chirilgan va u HAQIQATNI
			 * aytadi: backend `q` ni `employees.full_name`, `employees.id_code`
			 * va `users.phone` ga qo'llaydi (`employees.repository.ts` dagi
			 * `or(...)`). Uni toraytirish yoki kengaytirish backend bilan
			 * ajralishni bildiradi.
			 *
			 * ⚠️ `key` — `filters.q`: «Filtrlarni tozalash» dan keyin
			 * boshqarilmaydigan input o'z matnini saqlab qolardi.
			 */}
			<Input.Search
				key={filters.q ?? ""}
				placeholder={t("filterBar.searchPlaceholder")}
				allowClear
				defaultValue={filters.q}
				style={{ width: 250 }}
				onSearch={(value) => {
					patch({ q: value.trim() || undefined });
				}}
			/>

			<Select
				placeholder={t("filterBar.department")}
				allowClear
				showSearch={{ optionFilterProp: "label" }}
				style={{ width: 200 }}
				className={chipClass(Boolean(filters.departmentId))}
				options={departments.map((d) => ({ value: d.id, label: d.name }))}
				value={filters.departmentId}
				labelRender={(item) => t("filterBar.departmentChip", { qiymat: item.label })}
				onChange={(value?: string) => {
					patch({ departmentId: value });
				}}
			/>

			<Select
				placeholder={t("filterBar.status")}
				allowClear
				style={{ width: 180 }}
				className={chipClass(Boolean(filters.hrStatus))}
				options={hrStatusOptions}
				value={filters.hrStatus}
				labelRender={(item) => t("filterBar.statusChip", { qiymat: item.label })}
				onChange={(value?: EmployeeFilters["hrStatus"]) => {
					patch({ hrStatus: value });
				}}
			/>

			<Select
				placeholder={t("filterBar.position")}
				allowClear
				showSearch={{ optionFilterProp: "label" }}
				style={{ width: 200 }}
				className={chipClass(Boolean(filters.positionId))}
				options={positions.map((p) => ({ value: p.id, label: p.name }))}
				value={filters.positionId}
				labelRender={(item) => t("filterBar.positionChip", { qiymat: item.label })}
				onChange={(value?: string) => {
					patch({ positionId: value });
				}}
			/>

			{/*
			 * ⚠️ «Hudud» filtri `zones:read` darvozasi ostida — variantlar
			 * `GET /zones` dan keladi. «Faqat xavflilar» esa qo'shimcha
			 * ruxsat talab qilmaydi: `riskScore`/`riskFlag` `employees:read`
			 * javobida keladi.
			 */}
			<PermissionGate resource="zones" action="read">
				<Select
					placeholder={t("filterBar.zone")}
					allowClear
					showSearch={{ optionFilterProp: "label" }}
					style={{ width: 200 }}
					className={chipClass(Boolean(filters.zoneId))}
					options={zones.map((z) => ({ value: z.id, label: z.name }))}
					value={filters.zoneId}
					labelRender={(item) => t("filterBar.zoneChip", { qiymat: item.label })}
					onChange={(value?: string) => {
						patch({ zoneId: value });
					}}
				/>
			</PermissionGate>

			<Select
				placeholder={t("filterBar.risk")}
				allowClear
				style={{ width: 180 }}
				className={chipClass(filters.riskFlagged === true)}
				options={riskOptions}
				value={filters.riskFlagged === true ? "true" : undefined}
				labelRender={(item) => t("filterBar.riskChip", { qiymat: item.label })}
				onChange={(value?: string) => {
					patch({ riskFlagged: value === "true" ? true : undefined });
				}}
			/>

			{/* ⚠️ Faqat filtr BOR bo'lganda ko'rinadi va `onChange({})` chaqiradi
			    — `page`/`limit` ham tushadi, URL butunlay bo'shaydi. */}
			{activeFilterCount(filters) > 0 ? (
				<Button
					type="text"
					className="ml-auto"
					onClick={() => {
						onChange({});
					}}
				>
					{t("filterBar.clear")}
				</Button>
			) : null}
		</div>
	);
}
